/**
 * DatePicker – date-only calendar popover (professional styling, no native input).
 * Value/onChange: 'YYYY-MM-DD'. Radix Popover + date-fns + CalendarGrid.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback, useState } from 'react';
import { format, isValid, parse } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';

import { cn } from '@/lib/utils';

import { CalendarGrid } from './CalendarGrid';

export interface DatePickerProps {
  /** Selected date as 'YYYY-MM-DD'. */
  value: string;
  /** Called with the newly selected 'YYYY-MM-DD'. */
  onChange: (value: string) => void;
  /** Trigger className (e.g. to blend into a button group). */
  className?: string;
  disabled?: boolean;
  ariaLabel?: string;
  placeholder?: string;
  /** Show a leading calendar icon in the trigger (default true). */
  icon?: boolean;
  /** Show the footer "Today" shortcut (default true). */
  showToday?: boolean;
}

function parseYmd(v: string): Date | null {
  if (!v?.trim()) return null;
  const d = parse(v.slice(0, 10), 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : null;
}

export const DatePicker: FC<DatePickerProps> = ({
  value,
  onChange,
  className,
  disabled,
  ariaLabel = 'Select date',
  placeholder = 'Select date',
  icon = true,
  showToday = true,
}) => {
  const date = parseYmd(value);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(() => date ?? new Date());

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) setViewMonth(date ?? new Date());
    },
    [date],
  );

  const select = useCallback(
    (d: Date) => {
      onChange(format(d, 'yyyy-MM-dd'));
      setOpen(false);
    },
    [onChange],
  );

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            'flex items-center gap-2 text-sm font-medium text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            !date && 'font-normal text-muted-foreground',
            className,
          )}
        >
          {icon && <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />}
          <span className="truncate tabular-nums">
            {date ? format(date, 'dd/MM/yyyy') : placeholder}
          </span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          className={cn(
            'z-[250] w-auto rounded-xl border border-border bg-popover p-3 text-popover-foreground shadow-lg',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <CalendarGrid
            viewMonth={viewMonth}
            onViewMonthChange={setViewMonth}
            selectedDate={date}
            onDaySelect={select}
          />
          {showToday && (
            <div className="mt-2 flex items-center justify-between border-t border-border/60 pt-2">
              <span className="text-[11px] text-muted-foreground">
                {date ? format(date, 'EEE, d MMM yyyy') : ''}
              </span>
              <button
                type="button"
                onClick={() => select(new Date())}
                className="rounded-md px-2 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Today
              </button>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default DatePicker;
